import { EMPTY_TEXT_TRACKS, EMPTY_TIME_RANGES, MediaError } from "@videojs/media";
import { MediaPlayedRangesMixin, createTimeRange, serializeEmbedParams } from "@videojs/media/dom";
import { createPublicPromise, tryCall } from "@videojs/utils/function";
import { deepEqual } from "@videojs/utils/object";
import { isNull, isString, isUndefined } from "@videojs/utils/predicate";
import VimeoPlayer from "@vimeo/player";
//#region src/adapter.ts
/**
* @fires sourcechange - Fired when `source` changes, either directly or by resolving a new `src`. Read `source` for the
*   new value.
* @fires contentdatachange - Fired when the embed reports a title and when that title is cleared. Read `contentData`
*   for the new value.
*/
var VimeoAdapter = class VimeoAdapter extends MediaPlayedRangesMixin(EventTarget) {
	static {
		this.defaultProps = {
			src: "",
			autoplay: false,
			defaultMuted: false,
			muted: false,
			loop: false,
			controls: false,
			playsInline: true,
			preload: "metadata",
			poster: "",
			source: null
		};
	}
	#target = null;
	#player = null;
	#loadComplete = createPublicPromise();
	#src = VimeoAdapter.defaultProps.src;
	#autoplay = VimeoAdapter.defaultProps.autoplay;
	#defaultMuted = VimeoAdapter.defaultProps.defaultMuted;
	#loop = VimeoAdapter.defaultProps.loop;
	#controls = VimeoAdapter.defaultProps.controls;
	#playsInline = VimeoAdapter.defaultProps.playsInline;
	#preload = VimeoAdapter.defaultProps.preload;
	#poster = VimeoAdapter.defaultProps.poster;
	#source = VimeoAdapter.defaultProps.source;
	#paused = true;
	#ended = false;
	#seeking = false;
	#currentTime = 0;
	#duration = NaN;
	#volume = 1;
	#muted = false;
	#playbackRate = 1;
	#progress = 0;
	#videoWidth = NaN;
	#videoHeight = NaN;
	#readyState = READY_STATE_HAVE_NOTHING;
	#title = "";
	#contentData = {};
	#error = null;
	#isFullscreen = false;
	#isPictureInPicture = false;
	#disablePictureInPicture = false;
	#textTracksHost = null;
	#textTracksDisconnect = null;
	static {
		this.PLAYER_SOFTWARE_NAME = "vimeo-video";
	}
	/** Underlying `@vimeo/player` instance. Null until an embed URL can be resolved, which may be after `attach()`. */
	get engine() {
		return this.#player;
	}
	get target() {
		return this.#target;
	}
	/** Bind the iframe hosting the embed. The player follows once an embed URL resolves, maybe not until `load()`. */
	attach(target) {
		if (!target || this.#target === target) return;
		if (this.#target) this.detach();
		this.#target = target;
		this.#beginLoad();
		this.#createPlayer();
	}
	detach() {
		if (!this.#target) return;
		this.#teardownTextTracks();
		this.#player?.destroy().catch(() => {});
		this.#player = null;
		this.#target = null;
		this.#loadComplete.resolve();
		this.#resetState();
	}
	destroy() {
		this.detach();
		super.destroy();
	}
	get src() {
		return this.#src;
	}
	/** Vimeo URL or id. Setting it re-derives `source`, carrying its embed options over. */
	set src(value) {
		const { engine } = this.#source ?? {};
		const next = {
			...engine && { engine },
			...value && { src: value }
		};
		this.source = Object.keys(next).length > 0 ? next : null;
	}
	get currentSrc() {
		return this.#target?.getAttribute("src") ?? "";
	}
	get readyState() {
		return this.#readyState;
	}
	/** Reload the current source via Vimeo's `loadVideo`; no-op until `attach()`. */
	async load() {
		if (!this.#player) {
			if (!this.#target) return;
			const load = this.#beginLoad();
			this.#resetState();
			await Promise.resolve();
			if (load !== this.#loadComplete) return;
			this.#createPlayer();
			return;
		}
		const load = this.#beginLoad();
		this.#resetState();
		this.dispatchEvent(new Event("emptied"));
		if (!this.#src) {
			load.resolve();
			await this.#player.unload().catch(() => {});
			return;
		}
		this.dispatchEvent(new Event("loadstart"));
		const loadOptions = toLoadVideoOptions(this.#src, this.#source?.engine?.vimeo);
		if (!loadOptions) {
			load.resolve();
			return;
		}
		await this.#player.loadVideo(loadOptions).catch(() => {});
	}
	#beginLoad() {
		this.#loadComplete.resolve();
		this.#loadComplete = createPublicPromise();
		return this.#loadComplete;
	}
	#createPlayer() {
		const target = this.#target;
		if (!target || this.#player) return false;
		if (!target.getAttribute("src")) {
			const initialSrc = buildVimeoIframeSrc(this.#src, this.#snapshotProps());
			if (!initialSrc) {
				this.#loadComplete.resolve();
				return false;
			}
			target.src = initialSrc;
		}
		try {
			this.#player = new VimeoPlayer(target);
		} catch {
			this.#error = new MediaError("The attached iframe is not a Vimeo embed.", MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED);
			this.dispatchEvent(new Event("error"));
			this.#loadComplete.resolve();
			return false;
		}
		this.#bindPlayerEvents(this.#player);
		this.#setupTextTracks(this.#player);
		this.dispatchEvent(new Event("loadstart"));
		return true;
	}
	get paused() {
		return this.#paused;
	}
	get ended() {
		return this.#ended;
	}
	get seeking() {
		return this.#seeking;
	}
	async play() {
		await this.#loadComplete;
		await this.#player?.play();
	}
	pause() {
		this.#player?.pause().catch(() => {});
	}
	get currentTime() {
		return this.#currentTime;
	}
	set currentTime(value) {
		if (this.#currentTime === value) return;
		this.#currentTime = value;
		this.#afterLoad((p) => p.setCurrentTime(value));
	}
	get duration() {
		return this.#duration;
	}
	get volume() {
		return this.#volume;
	}
	set volume(value) {
		if (this.#volume === value) return;
		this.#volume = value;
		this.#afterLoad((p) => p.setVolume(value));
	}
	get muted() {
		return this.#muted;
	}
	set muted(value) {
		if (this.#muted === value) return;
		this.#muted = value;
		this.#afterLoad((p) => p.setMuted(value));
	}
	get playbackRate() {
		return this.#playbackRate;
	}
	set playbackRate(value) {
		if (this.#playbackRate === value) return;
		this.#playbackRate = value;
		this.#afterLoad((p) => p.setPlaybackRate(value));
	}
	get autoplay() {
		return this.#autoplay;
	}
	set autoplay(value) {
		this.#autoplay = value;
	}
	get defaultMuted() {
		return this.#defaultMuted;
	}
	set defaultMuted(value) {
		this.#defaultMuted = value;
	}
	get loop() {
		return this.#loop;
	}
	set loop(value) {
		this.#loop = value;
		this.#afterLoad((p) => p.setLoop(value));
	}
	get controls() {
		return this.#controls;
	}
	set controls(value) {
		this.#controls = value;
	}
	get playsInline() {
		return this.#playsInline;
	}
	set playsInline(value) {
		this.#playsInline = value;
	}
	get preload() {
		return this.#preload;
	}
	set preload(value) {
		this.#preload = value;
	}
	get poster() {
		return this.#poster;
	}
	set poster(value) {
		this.#poster = value;
	}
	/** Structured source: `src` plus embed options under `engine.vimeo`. Replacing it re-derives `src`. */
	get source() {
		return this.#source;
	}
	set source(value) {
		const source = value ?? null;
		if (source === this.#source) return;
		const src = source?.src ?? "";
		const srcChanged = this.#src !== src;
		const engineChanged = !deepEqual(this.#source?.engine?.vimeo ?? null, source?.engine?.vimeo ?? null);
		this.#source = source;
		this.#src = src;
		if (srcChanged || engineChanged) this.load();
		this.dispatchEvent(new Event("sourcechange"));
	}
	/**
	* Metadata Vimeo reports about the loaded video, keyed by what it is — `title` for now. Unlike a Mux source, none of
	* it can be derived from `src`; the embed has to report it, so the key is absent until then and empties again across
	* a source change. `contentdatachange` announces both.
	*/
	get contentData() {
		return this.#contentData;
	}
	/**
	* Store the title the embed reported, reporting whether the content data changed. Announcing is left to the caller,
	* which knows when the rest of what it is writing is in step.
	*
	* Vimeo cannot tell "no title yet" from "the title is blank" — a failed read falls back to the current value — and a
	* blank would read as a deliberate one, stopping a consumer's fallback chain. So an empty title is reported as an
	* absent key rather than an empty string.
	*/
	#setTitle(value) {
		if (this.#title === value) return false;
		this.#title = value;
		this.#contentData = value ? { title: value } : {};
		return true;
	}
	get buffered() {
		return this.#progress > 0 ? createTimeRange(0, this.#progress) : EMPTY_TIME_RANGES;
	}
	get seekable() {
		return this.#duration > 0 && Number.isFinite(this.#duration) ? createTimeRange(0, this.#duration) : EMPTY_TIME_RANGES;
	}
	get error() {
		return this.#error;
	}
	get textTracks() {
		this.#textTracksHost ??= globalThis.document?.createElement("video") ?? null;
		return this.#textTracksHost?.textTracks ?? EMPTY_TEXT_TRACKS;
	}
	get videoWidth() {
		return this.#videoWidth;
	}
	get videoHeight() {
		return this.#videoHeight;
	}
	get isFullscreen() {
		return this.#isFullscreen;
	}
	async requestFullscreen() {
		await this.#loadComplete;
		await this.#player?.requestFullscreen?.();
		this.#isFullscreen = true;
	}
	async exitFullscreen() {
		await this.#loadComplete;
		await this.#player?.exitFullscreen?.();
		this.#isFullscreen = false;
	}
	get isPictureInPicture() {
		return this.#isPictureInPicture;
	}
	get disablePictureInPicture() {
		return this.#disablePictureInPicture;
	}
	set disablePictureInPicture(value) {
		this.#disablePictureInPicture = value;
	}
	async requestPictureInPicture() {
		await this.#loadComplete;
		await this.#player?.requestPictureInPicture?.().then(() => {
			this.#isPictureInPicture = true;
		}, console.error);
	}
	async exitPictureInPicture() {
		await this.#loadComplete;
		await this.#player?.exitPictureInPicture?.().then(() => {
			this.#isPictureInPicture = false;
		}, console.error);
	}
	#afterLoad(fn) {
		this.#loadComplete.then(() => this.#player && void fn(this.#player).catch(() => {}), () => {});
	}
	#snapshotProps() {
		return {
			autoplay: this.#autoplay,
			defaultMuted: this.#defaultMuted,
			loop: this.#loop,
			controls: this.#controls,
			playsInline: this.#playsInline,
			preload: this.#preload || VimeoAdapter.defaultProps.preload,
			source: this.#source
		};
	}
	#resetState() {
		this.#currentTime = 0;
		this.#duration = NaN;
		this.#muted = false;
		this.#paused = !this.#autoplay;
		this.#ended = false;
		this.#playbackRate = 1;
		this.#progress = 0;
		this.#readyState = READY_STATE_HAVE_NOTHING;
		this.#seeking = false;
		this.#volume = 1;
		this.#error = null;
		this.#videoWidth = NaN;
		this.#videoHeight = NaN;
		this.#isFullscreen = false;
		this.#isPictureInPicture = false;
		if (this.#setTitle("")) this.dispatchEvent(new Event("contentdatachange"));
	}
	async #onLoaded() {
		const load = this.#loadComplete;
		this.#readyState = READY_STATE_HAVE_METADATA;
		const player = this.#player;
		let contentDataChanged = false;
		if (player) {
			const [muted, volume, duration, title] = await Promise.all([
				player.getMuted().catch(() => this.#muted),
				player.getVolume().catch(() => this.#volume),
				player.getDuration().catch(() => this.#duration),
				player.getVideoTitle().catch(() => this.#title)
			]);
			if (load !== this.#loadComplete) return;
			this.#muted = muted;
			this.#volume = volume;
			this.#duration = duration;
			contentDataChanged = this.#setTitle(title);
		}
		if (contentDataChanged) this.dispatchEvent(new Event("contentdatachange"));
		for (const type of [
			"loadedmetadata",
			"durationchange",
			"volumechange",
			"loadcomplete"
		]) this.dispatchEvent(new Event(type));
		load.resolve();
	}
	#bindPlayerEvents(player) {
		const emit = (type) => this.dispatchEvent(new Event(type));
		player.on("loaded", () => this.#onLoaded());
		player.on("bufferstart", () => emit("waiting"));
		player.on("play", () => {
			this.#paused = false;
			emit("play");
		});
		player.on("playing", () => {
			this.#readyState = READY_STATE_HAVE_FUTURE_DATA;
			this.#paused = false;
			emit("playing");
		});
		player.on("seeking", () => {
			this.#seeking = true;
			emit("seeking");
		});
		player.on("seeked", () => {
			this.#seeking = false;
			emit("seeked");
		});
		player.on("pause", () => {
			this.#paused = true;
			emit("pause");
		});
		player.on("ended", () => {
			this.#paused = true;
			this.#ended = true;
			emit("ended");
		});
		player.on("playbackratechange", ({ playbackRate }) => {
			this.#playbackRate = playbackRate;
			emit("ratechange");
		});
		player.on("volumechange", ({ volume }) => {
			this.#volume = volume;
			emit("volumechange");
		});
		player.on("durationchange", ({ duration }) => {
			this.#duration = duration;
			emit("durationchange");
		});
		player.on("timeupdate", ({ seconds, duration }) => {
			this.#currentTime = seconds;
			if (Number.isFinite(duration) && duration !== this.#duration) this.#duration = duration;
			emit("timeupdate");
		});
		player.on("progress", ({ seconds }) => {
			this.#progress = seconds;
			emit("progress");
		});
		player.on("resize", ({ videoWidth, videoHeight }) => {
			this.#videoWidth = videoWidth;
			this.#videoHeight = videoHeight;
			emit("resize");
		});
		player.on("fullscreenchange", ({ fullscreen }) => {
			this.#isFullscreen = fullscreen;
			emit("fullscreenchange");
		});
		player.on("enterpictureinpicture", () => {
			this.#isPictureInPicture = true;
			emit("enterpictureinpicture");
		});
		player.on("leavepictureinpicture", () => {
			this.#isPictureInPicture = false;
			emit("leavepictureinpicture");
		});
		player.on("error", () => {
			this.#error = {
				code: 1,
				message: "Vimeo playback error"
			};
			emit("error");
			this.#loadComplete.resolve();
		});
	}
	#setupTextTracks(player) {
		const doc = globalThis.document;
		if (isUndefined(doc)) return;
		this.#teardownTextTracks();
		const host = doc.createElement("video");
		this.#textTracksHost = host;
		player.getTextTracks().then((tracks) => {
			for (const track of tracks) {
				if (!isString(track.kind) || isNull(track.kind)) continue;
				tryCall(() => host.addTextTrack?.(track.kind, track.label ?? "", track.language ?? ""));
			}
		}).catch(() => {});
		this.#textTracksDisconnect = new AbortController();
		host.textTracks?.addEventListener?.("change", () => {
			const showing = Array.from(host.textTracks).find((t) => t.mode === "showing");
			if (showing) player.enableTextTrack(showing.language, showing.kind).catch(() => {});
			else player.disableTextTrack().catch(() => {});
		}, { signal: this.#textTracksDisconnect.signal });
	}
	#teardownTextTracks() {
		this.#textTracksDisconnect?.abort();
		this.#textTracksDisconnect = null;
		this.#textTracksHost = null;
	}
};
/** Extract a Vimeo video id from a numeric id, vimeo.com URL, or player URL. */
function parseVimeoVideoId(src) {
	return parseVimeoSource(src)?.id ?? null;
}
/**
* Parse a Vimeo source: a numeric id, `vimeo.com/<id>`, `vimeo.com/video/<id>`, `player.vimeo.com/video/<id>`, or
* `vimeo.com/event/<id>` (live events), plus unlisted/event hashes from `?h=` or a `/<hash>` segment.
*/
function parseVimeoSource(src) {
	if (!src) return null;
	if (/^\d+$/.test(src)) return {
		id: Number(src),
		kind: "video",
		hash: null
	};
	const match = MATCH_SRC.exec(src);
	if (!match) return null;
	const kind = match[1] === "event/" ? "event" : "video";
	let queryHash = null;
	try {
		queryHash = new URL(src).searchParams.get("h");
	} catch {}
	return {
		id: Number(match[2]),
		kind,
		hash: queryHash ?? match[3] ?? null
	};
}
/** Build the iframe `src` URL for an initial Vimeo embed from the given props. */
function buildVimeoIframeSrc(src, props = {}) {
	const parsed = parseVimeoSource(src);
	if (!parsed) return "";
	const params = {
		controls: props.controls === true ? null : 0,
		autoplay: props.autoplay,
		loop: props.loop,
		muted: props.defaultMuted,
		playsinline: props.playsInline ?? VimeoAdapter.defaultProps.playsInline,
		preload: props.preload ?? VimeoAdapter.defaultProps.preload,
		transparent: false,
		h: parsed.hash,
		...props.source?.engine?.vimeo ?? void 0
	};
	if (parsed.kind === "event") {
		const hashPath = parsed.hash ? `/${parsed.hash}` : "";
		delete params.h;
		return `${EMBED_EVENT_BASE}/${parsed.id}/embed${hashPath}?${serializeEmbedParams(params)}`;
	}
	return `${EMBED_VIDEO_BASE}/${parsed.id}?${serializeEmbedParams(params)}`;
}
const EMBED_VIDEO_BASE = "https://player.vimeo.com/video";
const EMBED_EVENT_BASE = "https://vimeo.com/event";
const MATCH_SRC = /vimeo\.com\/(video\/|event\/)?(\d+)(?:\/([\w-]+))?/;
const READY_STATE_HAVE_NOTHING = 0;
const READY_STATE_HAVE_METADATA = 1;
const READY_STATE_HAVE_FUTURE_DATA = 3;
function toLoadVideoOptions(src, vimeo) {
	const parsed = parseVimeoSource(src);
	if (!parsed) return null;
	return {
		url: `${parsed.kind === "event" ? `${EMBED_EVENT_BASE}/${parsed.id}/embed` : `${EMBED_VIDEO_BASE}/${parsed.id}`}${parsed.hash ? `?h=${parsed.hash}` : ""}`,
		...vimeo
	};
}
//#endregion
export { VimeoAdapter, buildVimeoIframeSrc, parseVimeoSource, parseVimeoVideoId };
